import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INT_CASINO_ROUTE, useAppSelector } from "@hypexone/uicore";
import { LANG_API_CLIENT } from "../../../App.utils";

const LOTTIE_SCRIPT_ID = "lottie-web-script";

const loadLottieScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if ((window as any).lottie) {
      resolve();
      return;
    }

    const existing = document.getElementById(LOTTIE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = LOTTIE_SCRIPT_ID;
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js";
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });

type FantasyLottieTileProps = {
  animationData: Record<string, unknown> | null;
};

const FantasyLottieTile = ({ animationData }: FantasyLottieTileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    if (!animationData || !containerRef.current) return;

    let cancelled = false;

    loadLottieScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;

        animRef.current = (window as any).lottie.loadAnimation({
          container: containerRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData,
          rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
          },
        });
      })
      .catch((error) => {
        console.error("Error loading lottie script:", error);
      });

    return () => {
      cancelled = true;
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [animationData]);

  const tileClassName =
    "w-full overflow-hidden h-[92px] md:h-auto md:aspect-[2.15/1]";

  if (!animationData) {
    return (
      <div
        className={`animate-pulse bg-[#F72424]/10 ${tileClassName}`}
      />
    );
  }

  return <div ref={containerRef} className={tileClassName} />;
};

const FantasySportsSection = ({games}) => {
  const navigate = useNavigate();
  const { langData } = useAppSelector((state) => state.cms);

  const handleProviderClick = (provider: any) => {
    if (provider?.providerName == "FANTASYHUB") {
      navigate('/dc/gamev1.1/cricket-fantasy-NTk1MDAx-ZmFudGFzeWh1Yl9jcmlja2V0X2ZhbnRhc3k=-RkFOVEFTWUhVQg==-RmFudGFzeUh1YiBHYW1pbmc=-R0FQ')
    } else if (provider?.gameId == null) {
      navigate({
        pathname: INT_CASINO_ROUTE,
        search: `?subProvider=${encodeURIComponent(provider?.providerName)}&category=${provider?.sectionCategory ? encodeURIComponent(provider?.sectionCategory) : 'ALL'}`,
      });
    } else {
      navigate(
        `/dc/gamev1.1/${provider?.gameName?.toLowerCase().replace(/\s+/g, "-")}-${btoa(
          provider?.gameId?.toString()
        )}-${btoa(provider?.gameCode)}-${btoa(provider)}-${btoa(provider?.subProvider)}-${btoa(provider?.superProvider)}`,
        {state: {gameName: provider?.gameName}}
      );
    }
  };

  return (
    <div className="flex flex-col gap-0.5 w-full overflow-hidden">
      <h2
        style={{
          background: "linear-gradient(-180deg, #F72424 0%, #BB1C00 100%)",
        }}
        className="text-[13px] md:text-[16px] px-1.5 py-1 text-white uppercase"
      >
        {langData?.["fantasy_and_fun"] || "Fantasy & Fun"}
      </h2>
      <div className="flex w-full gap-1">
        {games.map(game => 
          <div
            className="w-1/2 min-w-0 cursor-pointer overflow-hidden"
            onClick={() => handleProviderClick(game)}
          >
            <img src={game.sectionImage || game.trendingThumbnail || game.urlThumb}></img>
          </div>
        )}
      </div>
    </div>
  );
};

export default FantasySportsSection;
